import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Divider,
  IconButton,
  Badge,
  Avatar,
  Switch,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Slider,
  Alert,
  AlertTitle,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Tabs,
  Tab,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { colors } from '../theme';
import { Logo, LogoIcon } from '../components/Logo';
import MetricCard from '../components/cards/MetricCard';
import DataTable, { Column } from '../components/common/DataTable';
import { BarChart, BarChartCategory } from '../components/graphics/BarChart';

// Section component for consistent styling
const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({
  title,
  subtitle,
  children,
}) => {
  const theme = useTheme();
  return (
    <Box sx={{ mb: 6 }}>
      <Typography variant="h6" sx={{ mb: 0.5, color: theme.palette.text.primary }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ mb: 3, color: theme.palette.text.secondary }}>
          {subtitle}
        </Typography>
      )}
      {!subtitle && <Box sx={{ mb: 3 }} />}
      {children}
    </Box>
  );
};

// Color swatch component
const ColorSwatch: React.FC<{ color: string; name: string; hex?: string }> = ({ color, name, hex }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Box
      sx={{
        width: 64,
        height: 64,
        backgroundColor: color,
        borderRadius: 1,
        border: '1px solid rgba(255,255,255,0.1)',
        mb: 1,
      }}
    />
    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
      {name}
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', color: 'text.tertiary', fontSize: '0.65rem' }}>
      {hex || color}
    </Typography>
  </Box>
);

const KitchenSinkPage: React.FC = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [selectValue, setSelectValue] = useState('');
  const [switchValue, setSwitchValue] = useState(true);
  const [sliderValue, setSliderValue] = useState(50);

  // Sample data for DataTable
  const sampleTableData = [
    { id: 1, name: 'Alert 001', status: 'Open', severity: 'Critical', date: '2024-01-15' },
    { id: 2, name: 'Alert 002', status: 'In Progress', severity: 'High', date: '2024-01-14' },
    { id: 3, name: 'Alert 003', status: 'Resolved', severity: 'Medium', date: '2024-01-13' },
    { id: 4, name: 'Alert 004', status: 'Open', severity: 'Low', date: '2024-01-12' },
  ];

  const tableColumns: Column<typeof sampleTableData[0]>[] = [
    { key: 'id', header: 'ID', accessor: (row) => `#${row.id}` },
    { key: 'name', header: 'Name', accessor: (row) => row.name },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Chip
          label={row.status}
          size="small"
          // sx={{
          //   backgroundColor:
          //     row.status === 'Resolved'
          //       ? alpha(theme.palette.success.main, 0.15)
          //       : row.status === 'In Progress'
          //       ? alpha(theme.palette.warning.main, 0.15)
          //       : alpha(theme.palette.error.main, 0.15),
          //   color:
          //     row.status === 'Resolved'
          //       ? theme.palette.success.main
          //       : row.status === 'In Progress'
          //       ? theme.palette.warning.main
          //       : theme.palette.error.main,
          // }}
        />
      ),
    },
    {
      key: 'severity',
      header: 'Severity',
      render: (row) => {
        const severityColors: Record<string, string> = {
          Critical: '#DC2626',
          High: '#F97316',
          Medium: '#FBBF24',
          Low: '#10B981',
        };
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: severityColors[row.severity],
              }}
            />
            {row.severity}
          </Box>
        );
      },
    },
    { key: 'date', header: 'Date', accessor: (row) => row.date },
  ];

  // Sample data for BarChart
  const chartCategories: BarChartCategory[] = [
    {
      key: 'alerts',
      label: 'Alerts',
      color: '#8B5CF6',
      data: [
        { name: 'Mon', value: 12 },
        { name: 'Tue', value: 19 },
        { name: 'Wed', value: 8 },
        { name: 'Thu', value: 25 },
        { name: 'Fri', value: 15 },
        { name: 'Sat', value: 7 },
        { name: 'Sun', value: 4 },
      ],
    },
    {
      key: 'tickets',
      label: 'Tickets',
      color: '#10B981',
      data: [
        { name: 'Mon', value: 5 },
        { name: 'Tue', value: 8 },
        { name: 'Wed', value: 12 },
        { name: 'Thu', value: 6 },
        { name: 'Fri', value: 9 },
        { name: 'Sat', value: 3 },
        { name: 'Sun', value: 2 },
      ],
    },
  ];

  const c = colors.dark;

  return (
    <Box sx={{ p: 4, maxWidth: 1400, margin: '0 auto' }}>
      {/* Page Header */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Kitchen Sink
        </Typography>
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
          A comprehensive showcase of all UI components, colors, typography, and design elements.
        </Typography>
      </Box>

      {/* ===== BRANDING ===== */}
      <Section title="Branding" subtitle="Logo variants and brand elements">
        <Box sx={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" sx={{ mb: 1, display: 'block', color: 'text.tertiary' }}>
              Full Logo (Dark)
            </Typography>
            <Logo size={40} variant="dark" />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ mb: 1, display: 'block', color: 'text.tertiary' }}>
              Logo Icon Only
            </Typography>
            <LogoIcon size={48} />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ mb: 1, display: 'block', color: 'text.tertiary' }}>
              Large Logo
            </Typography>
            <Logo size={60} />
          </Box>
        </Box>
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ===== COLORS ===== */}
      <Section title="Color Palette" subtitle="Primary, semantic, and UI colors from the dark theme">
        {/* Primary Colors */}
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          PRIMARY (VIOLET)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
          <ColorSwatch color={c.primary[50]} name="50" />
          <ColorSwatch color={c.primary[100]} name="100" />
          <ColorSwatch color={c.primary[200]} name="200" />
          <ColorSwatch color={c.primary[300]} name="300" />
          <ColorSwatch color={c.primary[400]} name="400" />
          <ColorSwatch color={c.primary[500]} name="500" hex="#8B5CF6" />
          <ColorSwatch color={c.primary[600]} name="600" />
          <ColorSwatch color={c.primary[700]} name="700" />
          <ColorSwatch color={c.primary[800]} name="800" />
          <ColorSwatch color={c.primary[900]} name="900" />
        </Box>

        {/* Semantic Colors */}
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          SEMANTIC COLORS
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 4 }}>
          <ColorSwatch color={c.success[500]} name="Success" hex="#10B981" />
          <ColorSwatch color={c.warning[500]} name="Warning" hex="#F59E0B" />
          <ColorSwatch color={c.error[500]} name="Error" hex="#EF4444" />
          <ColorSwatch color={c.info[500]} name="Info" hex="#3B82F6" />
        </Box>

        {/* Background Colors */}
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          BACKGROUNDS
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
          <ColorSwatch color={c.background.primary} name="Primary" hex="#09090B" />
          <ColorSwatch color={c.background.secondary} name="Secondary" hex="#121217" />
          <ColorSwatch color={c.background.tertiary} name="Tertiary" hex="#1C1C21" />
          <ColorSwatch color={c.background.elevated} name="Elevated" hex="#26262C" />
          <ColorSwatch color={c.background.inset} name="Inset" hex="#050507" />
        </Box>

        {/* Text Colors */}
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          TEXT
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, backgroundColor: c.text.primary, borderRadius: 0.5 }} />
            <Typography variant="body2">Primary</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, backgroundColor: c.text.secondary, borderRadius: 0.5 }} />
            <Typography variant="body2">Secondary</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, backgroundColor: c.text.tertiary, borderRadius: 0.5 }} />
            <Typography variant="body2">Tertiary</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, backgroundColor: c.text.disabled, borderRadius: 0.5 }} />
            <Typography variant="body2">Disabled</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 16, backgroundColor: c.text.brand, borderRadius: 0.5 }} />
            <Typography variant="body2">Brand</Typography>
          </Box>
        </Box>
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ===== TYPOGRAPHY ===== */}
      <Section title="Typography" subtitle="Font styles and text hierarchy">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h4">Heading 4 - Inter 600</Typography>
          <Typography variant="h5">Heading 5 - Inter 600</Typography>
          <Typography variant="h6">Heading 6 - Inter 600 (1rem)</Typography>
          <Typography variant="subtitle1">Subtitle 1 - Regular body subtitle</Typography>
          <Typography variant="subtitle2">SUBTITLE 2 - UPPERCASE LABEL</Typography>
          <Typography variant="body1">Body 1 - Primary body text for main content</Typography>
          <Typography variant="body2">Body 2 - Secondary body text (0.875rem, 1.6 line height)</Typography>
          <Typography variant="caption">Caption - Monospace (JetBrains Mono)</Typography>
          <Typography variant="button">Button Text - 600 Weight</Typography>
        </Box>
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ===== BUTTONS ===== */}
      <Section title="Buttons" subtitle="Button variants, sizes, and states">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Contained */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              CONTAINED
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="contained" color="primary">
                Primary
              </Button>
              <Button variant="contained" color="secondary">
                Secondary
              </Button>
              <Button variant="contained" color="success">
                Success
              </Button>
              <Button variant="contained" color="warning">
                Warning
              </Button>
              <Button variant="contained" color="error">
                Error
              </Button>
              <Button variant="contained" disabled>
                Disabled
              </Button>
            </Box>
          </Box>

          {/* Outlined */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              OUTLINED
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="outlined" color="primary">
                Primary
              </Button>
              <Button variant="outlined" color="secondary">
                Secondary
              </Button>
              <Button variant="outlined" color="success">
                Success
              </Button>
              <Button variant="outlined" color="error">
                Error
              </Button>
              <Button variant="outlined" disabled>
                Disabled
              </Button>
            </Box>
          </Box>

          {/* Text */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              TEXT
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="text" color="primary">
                Primary
              </Button>
              <Button variant="text" color="secondary">
                Secondary
              </Button>
              <Button variant="text" disabled>
                Disabled
              </Button>
            </Box>
          </Box>

          {/* With Icons */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              WITH ICONS
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="contained" startIcon={<AddIcon />}>
                Add New
              </Button>
              <Button variant="outlined" startIcon={<RefreshIcon />}>
                Refresh
              </Button>
              <Button variant="contained" color="error" startIcon={<DeleteIcon />}>
                Delete
              </Button>
              <Button variant="outlined" endIcon={<EditIcon />}>
                Edit
              </Button>
            </Box>
          </Box>

          {/* Sizes */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              SIZES
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="contained" size="small">
                Small
              </Button>
              <Button variant="contained" size="medium">
                Medium
              </Button>
              <Button variant="contained" size="large">
                Large
              </Button>
            </Box>
          </Box>
        </Box>
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ===== CHIPS ===== */}
      <Section title="Chips" subtitle="Chip variants for labels, tags, and status indicators">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              DEFAULT
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label="Default" />
              <Chip label="Primary" color="primary" />
              <Chip label="Success" color="success" />
              <Chip label="Warning" color="warning" />
              <Chip label="Error" color="error" />
              <Chip label="Info" color="info" />
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              OUTLINED
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label="Default" variant="outlined" />
              <Chip label="Primary" variant="outlined" color="primary" />
              <Chip label="Success" variant="outlined" color="success" />
              <Chip label="Error" variant="outlined" color="error" />
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              WITH ICONS & ACTIONS
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip icon={<PersonIcon />} label="User" />
              <Chip icon={<WarningIcon />} label="Alert" color="warning" />
              <Chip label="Deletable" onDelete={() => {}} />
              <Chip avatar={<Avatar>JD</Avatar>} label="John Doe" />
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              STATUS CHIPS (CUSTOM)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label="Critical"
                sx={{
                  //backgroundColor: alpha('#DC2626', 0.15),
                  color: '#FCA5A5',
                  borderColor: '#DC2626',
                }}
              />
              <Chip
                label="High"
                sx={{
                  //backgroundColor: alpha('#F97316', 0.15),
                  color: '#FDBA74',
                  borderColor: '#F97316',
                }}
              />
              <Chip
                label="Medium"
                sx={{
                  //backgroundColor: alpha('#FBBF24', 0.15),
                  color: '#FDE68A',
                  borderColor: '#FBBF24',
                }}
              />
              <Chip
                label="Low"
                sx={{
                  //backgroundColor: alpha('#10B981', 0.15),
                  color: '#6EE7B7',
                  borderColor: '#10B981',
                }}
              />
            </Box>
          </Box>
        </Box>
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ===== FORM COMPONENTS ===== */}
      <Section title="Form Components" subtitle="Input fields, selects, and form controls">
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 3 }}>
          <TextField label="Text Field" placeholder="Enter text..." variant="outlined" fullWidth />
          <TextField label="With Helper" helperText="Some helpful text" fullWidth />
          <TextField label="Error State" error helperText="This field has an error" fullWidth />
          <TextField label="Disabled" disabled value="Disabled input" fullWidth />
          <TextField label="Password" type="password" fullWidth />
          <TextField
            label="With Icon"
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.tertiary' }} />,
            }}
            fullWidth
          />
          <TextField label="Multiline" multiline rows={3} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Select Option</InputLabel>
            <Select value={selectValue} label="Select Option" onChange={(e) => setSelectValue(e.target.value)}>
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="option1">Option 1</MenuItem>
              <MenuItem value="option2">Option 2</MenuItem>
              <MenuItem value="option3">Option 3</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            TOGGLES & CHECKS
          </Typography>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControlLabel
              control={<Switch checked={switchValue} onChange={(e) => setSwitchValue(e.target.checked)} />}
              label="Toggle Switch"
            />
            <FormControlLabel control={<Checkbox defaultChecked />} label="Checkbox" />
            <FormControlLabel control={<Checkbox />} label="Unchecked" />
            <FormControlLabel control={<Checkbox disabled />} label="Disabled" />
          </Box>

          <RadioGroup row defaultValue="option1">
            <FormControlLabel value="option1" control={<Radio />} label="Option 1" />
            <FormControlLabel value="option2" control={<Radio />} label="Option 2" />
            <FormControlLabel value="option3" control={<Radio />} label="Option 3" />
          </RadioGroup>

          <Box sx={{ maxWidth: 400 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Slider: {sliderValue}
            </Typography>
            <Slider value={sliderValue} onChange={(_, v) => setSliderValue(v as number)} />
          </Box>
        </Box>
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ===== ICONS ===== */}
      <Section title="Icons" subtitle="Material Icons commonly used in the application">
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {[
            { icon: <DashboardIcon />, name: 'Dashboard' },
            { icon: <AssignmentIcon />, name: 'Assignment' },
            { icon: <WarningIcon />, name: 'Warning' },
            { icon: <PersonIcon />, name: 'Person' },
            { icon: <SettingsIcon />, name: 'Settings' },
            { icon: <RefreshIcon />, name: 'Refresh' },
            { icon: <AddIcon />, name: 'Add' },
            { icon: <DeleteIcon />, name: 'Delete' },
            { icon: <EditIcon />, name: 'Edit' },
            { icon: <SearchIcon />, name: 'Search' },
            { icon: <NotificationsIcon />, name: 'Notifications' },
            { icon: <CheckCircleIcon />, name: 'CheckCircle' },
            { icon: <ErrorIcon />, name: 'Error' },
            { icon: <InfoIcon />, name: 'Info' },
          ].map((item) => (
            <Tooltip key={item.name} title={item.name}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.border.light}`,
                  minWidth: 70,
                }}
              >
                <Box sx={{ color: theme.palette.text.secondary }}>{item.icon}</Box>
                <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                  {item.name}
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            ICON BUTTONS
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <IconButton>
              <SettingsIcon />
            </IconButton>
            <IconButton color="primary">
              <AddIcon />
            </IconButton>
            <IconButton color="error">
              <DeleteIcon />
            </IconButton>
            <Badge badgeContent={4} color="error">
              <IconButton>
                <NotificationsIcon />
              </IconButton>
            </Badge>
            <Badge badgeContent={12} color="primary">
              <IconButton>
                <AssignmentIcon />
              </IconButton>
            </Badge>
          </Box>
        </Box>
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ===== ALERTS ===== */}
      <Section title="Alerts" subtitle="Feedback messages and notifications">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="success">
            <AlertTitle>Success</AlertTitle>
            This is a success alert with a title.
          </Alert>
          <Alert severity="info">This is an info alert.</Alert>
          <Alert severity="warning">This is a warning alert.</Alert>
          <Alert severity="error">This is an error alert.</Alert>
          <Alert severity="info" variant="outlined">
            Outlined variant
          </Alert>
          <Alert severity="success" variant="filled">
            Filled variant
          </Alert>
        </Box>
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ===== PROGRESS ===== */}
      <Section title="Progress Indicators" subtitle="Loading states and progress bars">
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', mb: 3 }}>
          <CircularProgress size={24} />
          <CircularProgress size={32} />
          <CircularProgress size={40} />
          <CircularProgress size={40} color="secondary" />
          <CircularProgress size={40} variant="determinate" value={75} />
        </Box>
        <Box sx={{ maxWidth: 400 }}>
          <LinearProgress sx={{ mb: 2 }} />
          <LinearProgress variant="determinate" value={60} sx={{ mb: 2 }} />
          <LinearProgress variant="buffer" value={60} valueBuffer={80} />
        </Box>
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ===== TABS ===== */}
      <Section title="Tabs" subtitle="Navigation tabs">
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="Overview" />
          <Tab label="Alerts" />
          <Tab label="Tickets" />
          <Tab label="Settings" />
        </Tabs>
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography>Tab content for: {['Overview', 'Alerts', 'Tickets', 'Settings'][tabValue]}</Typography>
        </Paper>
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ===== METRIC CARDS ===== */}
      <Section title="Metric Cards" subtitle="Dashboard metric display cards">
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 3 }}>
          <MetricCard
            label="Critical Alerts"
            value={23}
            severity="critical"
            topClients={[
              { name: 'Acme Corp', count: 12 },
              { name: 'TechCo', count: 8 },
              { name: 'DataInc', count: 3 },
            ]}
          />
          <MetricCard
            label="High Priority"
            value={45}
            severity="high"
            topClients={[
              { name: 'CloudSys', count: 20 },
              { name: 'NetWare', count: 15 },
            ]}
          />
          <MetricCard label="Medium Alerts" value={89} severity="medium" />
          <MetricCard label="Resolved Today" value={156} severity="low" />
        </Box>
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ===== DATA TABLE ===== */}
      <Section title="Data Table" subtitle="Reusable table component with pagination">
        <DataTable
          columns={tableColumns}
          data={sampleTableData}
          rowKey={(row) => row.id}
          onRowClick={(row) => console.log('Clicked:', row)}
          showPagination
          defaultRowsPerPage={5}
        />
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ===== CHARTS ===== */}
      <Section title="Charts" subtitle="Data visualization with Recharts">
        <Paper sx={{ p: 3 }}>
          <BarChart
            categories={chartCategories}
            title="Weekly Activity"
            subtitle="Alerts and tickets over the past week"
            height={250}
          />
        </Paper>
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ===== AVATARS ===== */}
      <Section title="Avatars" subtitle="User and entity representation">
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Avatar>JD</Avatar>
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>AB</Avatar>
          <Avatar sx={{ bgcolor: theme.palette.success.main }}>CD</Avatar>
          <Avatar sx={{ bgcolor: theme.palette.error.main }}>EF</Avatar>
          <Avatar sx={{ width: 56, height: 56 }}>LG</Avatar>
          <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem' }}>SM</Avatar>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={<Box sx={{ width: 12, height: 12, bgcolor: '#10B981', borderRadius: '50%', border: '2px solid #121217' }} />}
          >
            <Avatar>ON</Avatar>
          </Badge>
        </Box>
      </Section>

      <Divider sx={{ my: 4 }} />

      {/* ===== SPACING & BORDERS ===== */}
      <Section title="Spacing & Borders" subtitle="Border radius and spacing tokens">
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {[4, 6, 8, 12, 16].map((radius) => (
            <Box key={radius} sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.border.main}`,
                  borderRadius: `${radius}px`,
                  mb: 1,
                }}
              />
              <Typography variant="caption">{radius}px</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            BORDER STYLES
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box
              sx={{
                p: 2,
                border: `1px solid ${c.border.light}`,
                borderRadius: 1,
              }}
            >
              <Typography variant="caption">Border Light</Typography>
            </Box>
            <Box
              sx={{
                p: 2,
                border: `1px solid ${c.border.main}`,
                borderRadius: 1,
              }}
            >
              <Typography variant="caption">Border Main</Typography>
            </Box>
            <Box
              sx={{
                p: 2,
                border: `1px solid ${c.border.strong}`,
                borderRadius: 1,
              }}
            >
              <Typography variant="caption">Border Strong</Typography>
            </Box>
            <Box
              sx={{
                p: 2,
                border: `2px solid ${c.border.focus}`,
                borderRadius: 1,
              }}
            >
              <Typography variant="caption">Border Focus</Typography>
            </Box>
          </Box>
        </Box>
      </Section>
    </Box>
  );
};

export default KitchenSinkPage;
